import { DatePipe } from '@angular/common';
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs';
import { AppService } from '../../app.service';
import { Customer } from '../../models/customer';
import { DiscountScheduleItem } from '../../models/customerFeeSchedule';
import { CustomerFeeSchedule } from '../../models/customerFeeSchedule';
import { CustomerFeeScheduleResponse } from '../../responses/customerFeeScheduleResponse';
import { AccountService } from '../../services/AccountService';

@Component({ standalone:false,
  selector: 'edit-fee-schedule-dialog',
  templateUrl: 'edit-fee-schedule-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['edit-fee-schedule-dialog.component.scss']
})
export class EditFeeScheduleDialogComponent {
  public title: string= "Fee Schedule";
  public message: string = "";
  public saveButtonText = "Save";
  public cancelButtonText = "Cancel";

  public customer!:Customer;
  public feeSchedule!:CustomerFeeSchedule;
  public feeScheduleForm: FormGroup;
  private initialFormValues:any;

  public busyLoadingFeeSchedule:boolean=false;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<EditFeeScheduleDialogComponent>, private accService:AccountService, private snackBar: MatSnackBar, public datepipe: DatePipe) {
    if(data){
      this.message = data.message || this.message;
      if (data.buttonText) {
        this.saveButtonText = data.buttonText.ok || this.saveButtonText;
        this.cancelButtonText = data.buttonText.cancel || this.cancelButtonText;
      }
      if (data.customer){
        this.customer=data.customer;
      }
    }
    this.feeScheduleForm = new FormGroup({
      feeType: new FormControl(AppService.feeTypes[0], Validators.compose([Validators.required,Validators.min(0)])), //right now we can default to the only fee type we support
      amount: new FormControl<number|undefined>(undefined, Validators.required),
      discountSchedule: new FormArray([])
    });
  }

  ngOnInit(){
    this.initialFormValues=this.feeScheduleForm.value;
    this.feeScheduleForm.setValidators(this.dateSequenceValidation());
    this.prepopulate();
  }

  get availableFeeTypes(){
    return AppService.feeTypes;
  }

  get discountScheduleControls(): FormGroup[] {
    return (this.feeScheduleForm.get('discountSchedule') as FormArray).controls as FormGroup[];
  }

  public selectedFeeType($event:any){
    //nothing for now
  }

  private prepopulate(){
    this.busyLoadingFeeSchedule=true;
    this.feeScheduleForm.reset(this.initialFormValues);
    this.deleteAllDiscountScheduleItems();
    let feeType = this.feeScheduleForm.get("feeType")!.value;
    if(feeType?.billingTypeCode) this.accService.getCustomerFeeSchedule(this.customer.id,feeType?.billingTypeCode).pipe(take(1)).subscribe(cfs=>{
      if(cfs){
        this.feeSchedule=cfs;
        this.feeScheduleForm.get('amount')!.setValue(cfs.amount);
        this.addDiscountSchedule(cfs.discountSchedule);//create parameters from definition
        this.busyLoadingFeeSchedule=false;
        this.feeScheduleForm.markAsPristine();
      }
    });
  }

  public createDiscountScheduleItem(dsi:DiscountScheduleItem): FormGroup {
    let fg =  new FormGroup({
       seqNo: new FormControl<number>(dsi.seqNo, Validators.required), //don't show in ui; serves as link between parameter and default value of parameter
       discountPercent: new FormControl<number>(dsi.discountPercent, Validators.compose([Validators.required, Validators.max(100), Validators.min(0)])),
       until: new FormControl(this.datepipe.transform(dsi.until, 'yyyy-MM-dd'), Validators.required)
    });
    
    return fg
  }

  public addDiscountSchedule(discountSchedule:DiscountScheduleItem[]) {
    if(discountSchedule.length>0){
      const fa = this.feeScheduleForm.get('discountSchedule') as FormArray;
      discountSchedule.forEach(dsi=>{
        let fg=this.createDiscountScheduleItem(dsi);
        fa.push(fg);
      });
    }
  }

  public addDiscountScheduleItem() {
    const fa = this.feeScheduleForm.get('discountSchedule') as FormArray;
    let fg =  new FormGroup({
      seqNo: new FormControl<number>(fa.length+1, Validators.required),
      discountPercent: new FormControl<number>(0, Validators.compose([Validators.required, Validators.max(100), Validators.min(0)])),
      until: new FormControl(null, Validators.required)
    });
    fa.push(fg);
    fa.markAsTouched();
    fg.updateValueAndValidity();
    this.feeScheduleForm.markAsDirty();
  }

  public deleteDiscountScheduleItem(index:number){
    const fa = this.feeScheduleForm.get('discountSchedule') as FormArray;
    fa.removeAt(index);
    fa.markAsTouched();
    this.feeScheduleForm.markAsDirty();
  }

  private deleteAllDiscountScheduleItems(){
    const fa = this.feeScheduleForm.get('discountSchedule') as FormArray;
    fa.controls=[];
  }

  public discountedAmount(percentage:number){
    return (this.feeScheduleForm.controls.amount.value - Math.round(this.feeScheduleForm.controls.amount.value * (percentage/100)));
  }
 
  get canSave(){
    return this.feeScheduleForm.dirty && this.feeScheduleForm.valid;
  }

  private createTempFeeSchedule(){
    let fs =new CustomerFeeSchedule();
    fs.id=this.feeSchedule.id;
    fs.customerId=this.feeSchedule.customerId;
    fs.billingTypeCode=this.feeSchedule.billingTypeCode;
    fs.amount = this.feeScheduleForm.get('amount')!.value;
    fs.added=this.feeSchedule.added;
    fs.edited=this.feeSchedule.edited;
    const fa = this.feeScheduleForm.get('discountSchedule') as FormArray;
    const now = new Date();
    let seqNo=1;
    fa.controls.forEach(fg=>{
      let dsi=new DiscountScheduleItem();
      dsi.seqNo=seqNo;
      dsi.discountPercent=fg.get("discountPercent")!.value;
      dsi.until=new Date(new Date(fg.get("until")!.value).getTime()+86399999); //adding 23:59:59.999 seconds to set end of day time
      fs.discountSchedule.push(dsi);
      seqNo++
    });
    return fs;
  }

  onSaveClick(): void {
    this.busyLoadingFeeSchedule=true;
    let tempFs = this.createTempFeeSchedule();
    this.accService.updateCustomerFeeSchedule(tempFs).pipe(take(1)).subscribe((r:CustomerFeeScheduleResponse)=>{
      if(r.success){
        this.snackBar.open("Fee schedule has been saved", '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        this.feeSchedule = CustomerFeeSchedule.mapFromDTO(r.customerFeeSchedule!);
        this.prepopulate();
      }
      else{
        this.snackBar.open("Error saving fee schedule for customer '" + this.customer.name + "': "  + r.message, 'x', { panelClass: ['warn-snackbar'], verticalPosition: 'top' });
      }
      this.busyLoadingFeeSchedule=false;
    });
  }

  onCancelClick(): void {
    this.dialogRef.close(undefined);
  }

  dateSequenceValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const fa = group.get('discountSchedule') as FormArray;
      let lastVerifiedDate: number | undefined;

      for (const fg of fa.controls) {
        const untilControl = (fg as FormGroup).get('until');
        if (!untilControl) continue;
        
        const verifyingDate = new Date(untilControl.value as string).getTime();
        if (lastVerifiedDate !== undefined && verifyingDate <= lastVerifiedDate) {
          untilControl.setErrors({ badDateSequence: true });
        } else {
          untilControl.setErrors(null);
        }
        lastVerifiedDate = verifyingDate;
      }
      return null;
    };
  }
}